import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Modal, Typography, Checkbox, Select, Table, Tag, Progress } from 'antd'
import type { ColumnsType } from 'antd/es/table'
import {
  ArrowRightLeft,
  Database,
  GitMerge,
  RefreshCw,
  Replace,
  Server,
} from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { itAdminApi } from '@/api/services'
import { useAuthStore } from '@/stores/authStore'
import type { ApprovalsSyncResult, ApprovalsSyncSourceDatabase, ApprovalsSyncJobProgress } from '@/types'
import { Button } from '@/components/ui/Button'
import {
  PageShell,
  PageHero,
  DashboardPanel,
  KpiCard,
  KpiCardGrid,
  KPI_TONE_COLORS,
  PageLoadingSkeleton,
} from '@/components/dashboard'
import { cn } from '@/utils/cn'

const { Paragraph, Text } = Typography

function formatCount(value: number | null | undefined): string {
  if (value == null || Number.isNaN(value)) return '—'
  return value.toLocaleString()
}

function formatSourceOption(
  db: ApprovalsSyncSourceDatabase,
  t: (key: string, fallback: string) => string
) {
  if (!db.hasApprovalsTable) {
    return `${db.name} (${t('ApprovalsSync:source.noTable', 'no approvals table')})`
  }
  return `${db.name} (${formatCount(db.rowCount)} ${t('ApprovalsSync:source.rows', 'rows')})`
}

const ApprovalsSyncPage: React.FC = () => {
  const { t } = useTranslation(['ApprovalsSync', 'Common'])
  const { user } = useAuthStore()
  const canSyncApprovals = Boolean(user?.permissions?.includes('pod.sync_approvals'))
  const qc = useQueryClient()
  const userPickedSource = useRef(false)
  const [selectedSource, setSelectedSource] = useState<string>('')
  const [replaceOpen, setReplaceOpen] = useState(false)
  const [replaceChecked, setReplaceChecked] = useState(false)
  const [lastResult, setLastResult] = useState<ApprovalsSyncResult | null>(null)
  const [syncProgress, setSyncProgress] = useState<ApprovalsSyncJobProgress | null>(null)

  const previewQuery = useQuery({
    queryKey: ['it-admin', 'approvals-sync', 'preview', selectedSource || '__default__'],
    queryFn: async () => {
      const res = await itAdminApi.getApprovalsSyncPreview(
        selectedSource ? { sourceDatabase: selectedSource } : undefined
      )
      if (!res.success || !res.data) {
        throw new Error(res.error || res.message || 'Failed to load preview')
      }
      return res.data
    },
  })

  const preview = previewQuery.data

  useEffect(() => {
    if (userPickedSource.current || !preview?.sourceDatabase) return
    setSelectedSource(preview.sourceDatabase)
  }, [preview?.sourceDatabase])

  const mergeMutation = useMutation({
    mutationFn: () =>
      itAdminApi.runApprovalsSync(
        { mode: 'merge', sourceDatabase: selectedSource },
        (progress) => setSyncProgress(progress)
      ),
    onSuccess: (res) => {
      if (!res.success || !res.data) {
        toast.error(res.error || res.message || t('ApprovalsSync:errors.syncFailed', 'Sync failed'))
        return
      }
      setLastResult(res.data)
      toast.success(t('ApprovalsSync:merge.success', 'Approvals merged from source'))
      void qc.invalidateQueries({ queryKey: ['it-admin', 'approvals-sync', 'preview'] })
    },
    onError: (err: Error) =>
      toast.error(err.message || t('ApprovalsSync:errors.syncFailed', 'Sync failed')),
    onSettled: () => setSyncProgress(null),
  })

  const replaceMutation = useMutation({
    mutationFn: () =>
      itAdminApi.runApprovalsSync(
        {
          mode: 'replace',
          sourceDatabase: selectedSource,
          confirmReplace: true,
        },
        (progress) => setSyncProgress(progress)
      ),
    onSuccess: (res) => {
      setReplaceOpen(false)
      setReplaceChecked(false)
      if (!res.success || !res.data) {
        toast.error(res.error || res.message || t('ApprovalsSync:errors.syncFailed', 'Sync failed'))
        return
      }
      setLastResult(res.data)
      toast.success(t('ApprovalsSync:replace.success', 'Approvals replaced from source'))
      void qc.invalidateQueries({ queryKey: ['it-admin', 'approvals-sync', 'preview'] })
    },
    onError: (err: Error) =>
      toast.error(err.message || t('ApprovalsSync:errors.syncFailed', 'Sync failed')),
    onSettled: () => setSyncProgress(null),
  })

  const busy = mergeMutation.isPending || replaceMutation.isPending
  const canSync = Boolean(canSyncApprovals && selectedSource && preview?.sourceTableExists)
  const rowDelta =
    preview?.sourceRowCount != null && preview?.podRowCount != null
      ? preview.sourceRowCount - preview.podRowCount
      : null

  const heroBadges = useMemo(
    () => [
      { label: `${t('ApprovalsSync:kpi.environment', 'Environment')}: ${preview?.environment ?? '—'}`, icon: Server },
      { label: `${t('ApprovalsSync:kpi.podDb', 'POD database')}: ${preview?.podDatabase ?? '—'}`, icon: Database },
    ],
    [preview?.environment, preview?.podDatabase, t]
  )

  const sourceColumns: ColumnsType<ApprovalsSyncSourceDatabase> = [
    {
      title: t('ApprovalsSync:table.database', 'Database'),
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => (
        <span className={cn('font-medium', name === selectedSource && 'text-primary')}>{name}</span>
      ),
    },
    {
      title: t('ApprovalsSync:table.approvalsTable', 'approvals table'),
      dataIndex: 'hasApprovalsTable',
      key: 'hasApprovalsTable',
      width: 140,
      render: (has: boolean) =>
        has ? (
          <Tag color="success">{t('ApprovalsSync:table.present', 'Present')}</Tag>
        ) : (
          <Tag>{t('ApprovalsSync:table.missing', 'Missing')}</Tag>
        ),
    },
    {
      title: t('ApprovalsSync:table.rowCount', 'Rows'),
      dataIndex: 'rowCount',
      key: 'rowCount',
      width: 120,
      align: 'right',
      render: (count: number | null) => (
        <span className="tabular-nums">{formatCount(count)}</span>
      ),
    },
    {
      title: '',
      key: 'action',
      width: 100,
      align: 'right',
      render: (_: unknown, row) => (
        <Button
          type="button"
          variant={row.name === selectedSource ? 'default' : 'outline'}
          className="h-8 px-3 text-xs"
          disabled={!row.hasApprovalsTable}
          onClick={() => {
            userPickedSource.current = true
            setSelectedSource(row.name)
          }}
        >
          {row.name === selectedSource
            ? t('ApprovalsSync:table.selected', 'Selected')
            : t('ApprovalsSync:table.use', 'Use')}
        </Button>
      ),
    },
  ]

  if (previewQuery.isLoading && !preview) {
    return (
      <PageShell>
        <PageLoadingSkeleton />
      </PageShell>
    )
  }

  return (
    <PageShell>
      <PageHero
        title={t('ApprovalsSync:header.title', 'Sync approvals')}
        subtitle={t(
          'ApprovalsSync:header.description',
          'Copy legacy approval rows from an auth database into POD until database cutover.'
        )}
        badges={heroBadges}
        actions={
          <Button
            type="button"
            variant="outline"
            onClick={() => void previewQuery.refetch()}
            disabled={previewQuery.isFetching || busy}
          >
            <RefreshCw className={cn('size-4', previewQuery.isFetching && 'animate-spin')} />
            {t('ApprovalsSync:actions.refresh', 'Refresh preview')}
          </Button>
        }
      />

      <div className="space-y-6">
        {!canSyncApprovals && (
          <Alert
            type="warning"
            showIcon
            message="View-Only Mode"
            description="You have view-only access. Running sync operations (Merge / Replace) is restricted."
          />
        )}

        {previewQuery.isError ? (
          <Alert
            type="error"
            showIcon
            message={t('ApprovalsSync:errors.previewFailed', 'Could not load sync preview')}
            description={
              previewQuery.error instanceof Error ? previewQuery.error.message : undefined
            }
          />
        ) : null}

        <DashboardPanel
          title={t('ApprovalsSync:sections.sourceTitle', 'Source auth database')}
          subtitle={t(
            'ApprovalsSync:sections.sourceSubtitle',
            'Databases on this MySQL server whose name contains "auth". Sync reads from approvals in the selected database.'
          )}
        >
          <div className="space-y-5">
            <div className="max-w-xl space-y-2">
              <label className="text-sm font-medium text-foreground" htmlFor="approvals-sync-source">
                {t('ApprovalsSync:source.label', 'Selected source')}
              </label>
              <Select
                id="approvals-sync-source"
                className="w-full"
                size="large"
                loading={previewQuery.isFetching}
                placeholder={t('ApprovalsSync:source.placeholder', 'Select auth database')}
                value={selectedSource || undefined}
                onChange={(value) => {
                  userPickedSource.current = true
                  setSelectedSource(value)
                }}
                options={(preview?.sourceDatabases ?? []).map((db) => ({
                  value: db.name,
                  label: formatSourceOption(db, (key, fallback) => t(key, fallback)),
                  disabled: !db.hasApprovalsTable,
                }))}
                notFoundContent={t(
                  'ApprovalsSync:source.empty',
                  'No auth databases found on this server'
                )}
              />
            </div>

            <Table
              size="small"
              rowKey="name"
              pagination={false}
              loading={previewQuery.isFetching}
              dataSource={preview?.sourceDatabases ?? []}
              columns={sourceColumns}
              rowClassName={(row) =>
                cn(row.name === selectedSource && 'bg-primary/5 hover:bg-primary/5')
              }
            />
          </div>
        </DashboardPanel>

        <DashboardPanel
          title={t('ApprovalsSync:sections.previewTitle', 'Sync preview')}
          subtitle={t(
            'ApprovalsSync:sections.previewSubtitle',
            'Compare row counts between the selected source and POD before running a sync.'
          )}
        >
          <KpiCardGrid className="grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
            <KpiCard
              title={t('ApprovalsSync:kpi.sourceDb', 'Source database')}
              count={preview?.sourceRowCount ?? 0}
              color={KPI_TONE_COLORS.blue}
              icon={Database}
            />
            <KpiCard
              title={t('ApprovalsSync:kpi.podRows', 'POD rows')}
              count={preview?.podRowCount ?? 0}
              color={KPI_TONE_COLORS.emerald}
              icon={Server}
            />
            <KpiCard
              title={t('ApprovalsSync:kpi.delta', 'Difference')}
              count={Math.abs(rowDelta ?? 0)}
              color={KPI_TONE_COLORS.amber}
              icon={ArrowRightLeft}
            />
            <KpiCard
              title={t('ApprovalsSync:kpi.sourceRows', 'Source rows')}
              count={preview?.sourceRowCount ?? 0}
              color={KPI_TONE_COLORS.purple}
              icon={GitMerge}
            />
          </KpiCardGrid>
        </DashboardPanel>

        {busy && syncProgress ? (
          <DashboardPanel
            title={t('ApprovalsSync:progress.title', 'Sync in progress')}
            subtitle={t(
              'ApprovalsSync:progress.subtitle',
              'Large databases are copied in batches. You can keep this page open until completion.'
            )}
          >
            <div className="space-y-3">
              <Progress
                percent={syncProgress.percent}
                status="active"
                strokeColor="#dc2925"
                format={(percent) => `${percent ?? 0}%`}
              />
              <p className="text-sm text-muted-foreground tabular-nums">
                {t('ApprovalsSync:progress.rows', '{{processed}} of {{total}} rows processed', {
                  processed: formatCount(syncProgress.processed),
                  total: formatCount(syncProgress.total),
                })}
              </p>
            </div>
          </DashboardPanel>
        ) : null}

        <div className="grid gap-4 lg:grid-cols-2">
          <DashboardPanel
            title={t('ApprovalsSync:merge.title', 'Merge from source')}
            subtitle={t(
              'ApprovalsSync:merge.description',
              'Upsert rows by id. Existing POD approvals are updated; new ids are inserted. Safe for repeated syncs.'
            )}
          >
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground">
                {t('ApprovalsSync:merge.note', 'Recommended while you are still cutting over from legacy auth databases.')}
              </p>
              <Button type="button" onClick={() => mergeMutation.mutate()} disabled={busy || !canSync}>
                <GitMerge className="size-4" />
                {mergeMutation.isPending
                  ? t('ApprovalsSync:merge.running', 'Merging…')
                  : t('ApprovalsSync:merge.action', 'Merge from source')}
              </Button>
            </div>
          </DashboardPanel>

          <DashboardPanel
            title={t('ApprovalsSync:replace.title', 'Replace from source')}
            subtitle={t(
              'ApprovalsSync:replace.panelDescription',
              'Truncates POD approvals and related bulk-upload tables, backs up current data, then copies all rows from the source.'
            )}
          >
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-sm text-muted-foreground">
                {t('ApprovalsSync:replace.note', 'Use only when you need a full reset of POD approval data from the selected source.')}
              </p>
              <Button
                type="button"
                variant="destructive"
                onClick={() => setReplaceOpen(true)}
                disabled={busy || !canSync}
              >
                <Replace className="size-4" />
                {t('ApprovalsSync:replace.action', 'Replace from source')}
              </Button>
            </div>
          </DashboardPanel>
        </div>

        {lastResult ? (
          <Alert
            type="success"
            showIcon
            message={t('ApprovalsSync:lastRun.title', 'Last sync completed')}
            description={
              <div className="grid gap-1 text-sm sm:grid-cols-2">
                <div>
                  {t('ApprovalsSync:lastRun.source', 'Source')}:{' '}
                  <Text strong>{lastResult.sourceDatabase}</Text>
                </div>
                <div>
                  {t('ApprovalsSync:lastRun.mode', 'Mode')}: <Text strong>{lastResult.mode}</Text>
                </div>
                {lastResult.affected != null ? (
                  <div>
                    {t('ApprovalsSync:lastRun.affected', 'Rows affected')}:{' '}
                    <Text strong>{formatCount(lastResult.affected)}</Text>
                  </div>
                ) : null}
                {lastResult.inserted != null ? (
                  <div>
                    {t('ApprovalsSync:lastRun.inserted', 'Rows inserted')}:{' '}
                    <Text strong>{formatCount(lastResult.inserted)}</Text>
                  </div>
                ) : null}
                {lastResult.backupTable ? (
                  <div className="sm:col-span-2">
                    {t('ApprovalsSync:lastRun.backup', 'Backup table')}:{' '}
                    <Text code>{lastResult.backupTable}</Text>
                  </div>
                ) : null}
                <div className="sm:col-span-2">
                  {t('ApprovalsSync:lastRun.completed', 'Completed')}: {lastResult.completedAt}
                </div>
              </div>
            }
          />
        ) : null}
      </div>

      <Modal
        open={replaceOpen}
        title={t('ApprovalsSync:replace.title', 'Replace POD approvals')}
        onCancel={() => {
          if (replaceMutation.isPending) return
          setReplaceOpen(false)
          setReplaceChecked(false)
        }}
        footer={[
          <Button
            key="cancel"
            type="button"
            variant="outline"
            onClick={() => {
              setReplaceOpen(false)
              setReplaceChecked(false)
            }}
            disabled={replaceMutation.isPending}
          >
            {t('Common:general.cancel', 'Cancel')}
          </Button>,
          <Button
            key="confirm"
            type="button"
            variant="destructive"
            disabled={!replaceChecked || replaceMutation.isPending}
            onClick={() => replaceMutation.mutate()}
          >
            {replaceMutation.isPending
              ? t('ApprovalsSync:replace.running', 'Replacing…')
              : t('ApprovalsSync:replace.confirmAction', 'Replace approvals')}
          </Button>,
        ]}
        destroyOnClose
      >
        <div className="space-y-4">
          <Paragraph type="secondary">
            {t(
              'ApprovalsSync:replace.description',
              'This truncates POD approvals, approval attachments, and bulk-upload queue/mutation tables, then copies all rows from the selected auth database. A backup table is created first.'
            )}
          </Paragraph>
          <ul className="list-disc space-y-1 pl-5 text-sm">
            <li>
              {t('ApprovalsSync:kpi.sourceDb', 'Source database')}:{' '}
              <strong>{preview?.sourceDatabase ?? '—'}</strong>
            </li>
            <li>
              {t('ApprovalsSync:kpi.sourceRows', 'Source rows')}:{' '}
              <strong>{formatCount(preview?.sourceRowCount)}</strong>
            </li>
            <li>
              {t('ApprovalsSync:kpi.podRows', 'POD rows')}:{' '}
              <strong>{formatCount(preview?.podRowCount)}</strong>
            </li>
          </ul>
          <Checkbox checked={replaceChecked} onChange={(e) => setReplaceChecked(e.target.checked)}>
            {t(
              'ApprovalsSync:replace.checkbox',
              'I understand this will wipe POD approval data and re-copy from the selected auth database'
            )}
          </Checkbox>
        </div>
      </Modal>
    </PageShell>
  )
}

export default ApprovalsSyncPage
